// polyfill at nodejs env
if (!Promise.withResolvers) {
  Promise.withResolvers = () => {
    let resolve, reject;
    const promise = new Promise((_resolve, _reject) => {
      resolve = _resolve;
      reject = _reject;
    });

    return {
      promise,
      resolve,
      reject
    };
  };
}

//  控制动画帧
export const raf = () => new Promise((resolve) => requestAnimationFrame(resolve));

//空闲时间任务
export const ric = (opts) => new Promise((resolve) => requestIdleCallback(resolve, opts));

// 普通模式：等待多少ms
export const sleep = (ms) => createSleep()({ ms });
//  普通模式带返回值：等待多少ms后返回v
export const sleepWith = (ms, v) => createSleep({ withRetVal: true })({ ms, v });
//  拒绝模式：等待多少ms后拒绝
export const rejectSleep = (ms) => createSleep({ isRejectMode: true })({ ms });
//  拒绝模式带返回值：等待多少ms后拒绝并返回v
export const rejectSleepWith = (ms, v) =>
  createSleep({ isRejectMode: true, withRetVal: true })({ ms, v });

// createSleep() 是一个工厂函数，提供不同模式的 sleep 实现
export const createSleep = ({ isRejectMode = false, withRetVal = false } = {}) => {
  return ({ ms, v }) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        Reflect.apply(
          isRejectMode ? reject : resolve,
          null,
          Array.of(v).flatMap((v) => (withRetVal ? [v] : []))
        );
      }, ms);
    });
  };
};

// 这是一个异步生成器，类似定时器，每隔 gap 毫秒生成一个递增的数
export async function* createTicker(gap) {
  let c = -1;
  while (true) {
    await sleep(gap);
    yield (c += 1);
  }
}

/**
 * @description 异步事件监听器
 * @param el
 * @param evtType
 * @returns {{[Symbol.asyncIterator](): AsyncGenerator<*, void, *>}}
 */
export function fromEvent(el, evtType) {
  const aq = new AsyncQueue();

  (function setup() {
    aq.enqueue();
  })();

  el.addEventListener(evtType, (evt) => {
    aq.resolve(evt);
    aq.enqueue();
  });

  return {
    async *[Symbol.asyncIterator]() {
      while (1) {
        yield await aq.dequeue();
      }
    }
  };
}

// 对异步可迭代对象进行映射
export async function* mapTo(ai, projection = (v) => v) {
  for await (const e of ai) {
    yield projection(e);
  }
}

// 当 condition(v, i) 为 true 时终止迭代
export async function* end(ai, condition = (_v, _i) => true) {
  let cnt = -1;
  for await (const v of ai) {
    if (condition(v, (cnt += 1))) {
      break;
    }
    yield v;
  }
}

const noop = () => {};

// 异步迭代器的辅助函数，用于等待所有异步迭代器完成
export const waitUntil = async (predicate) => {
  // eslint-disable-next-line no-constant-condition
  while (1) {
    if (predicate()) {
      break;
    }

    await CoroutineManager.WaitForEndOfFrame();
  }
};

const createArrayWith = (len, v) => {
  let arr = Array.from({ length: len });
  if (typeof v === "function") {
    return arr.map((_, i) => v(i));
  }
  return arr.fill(v);
};

//  异步队列，结合 Promise 进行数据流控制
export class AsyncQueue {
  constructor() {
    /**
     * @type {Promise[]}
     * 在头部可以使用一个promise 卡住,让异步迭代器无法完成
     */
    this.promiseQueue = [];

    this._resolve = null;
  }

  get resolve() {
    return this._resolve;
  }

  enqueue() {
    const { promise, resolve } = Promise.withResolvers();
    this.promiseQueue.push(promise);
    this._resolve = resolve;
  }

  /**
   * @returns {Promise}
   */
  dequeue() {
    return this.promiseQueue.shift();
  }

  async *[Symbol.asyncIterator]() {
    let v;
    while ((v = await this.dequeue())) {
      yield v;
    }
  }
}

//  Simple Channel
export class Chan {
  constructor() {
    this.aq = new AsyncQueue();

    this.aq.enqueue();
  }

  static END_TOKEN = Symbol("END_TOKEN");

  //  same as chan <- v in golang
  send(v) {
    //  never block, without limit of queue cnt
    this.aq.resolve(v);
    this.aq.enqueue();
  }

  close() {
    this.aq.dequeue();
    this.aq.resolve(Chan.END_TOKEN);
  }

  //  solitude consumer
  //  same as chan -> v in golang
  async receive() {
    const e = await this.aq.dequeue();
    if (e === Chan.END_TOKEN) {
      return;
    }
    return e;
  }

  async *consumer() {
    let v;
    while ((v = await this.receive())) {
      yield v;
    }
  }
}

export const ProdCons = Chan;

/**
 * @description multi selection 选择一个最新有值的生产者,多路复用
 * @param {[]} asyncIterList - 上游生产者列表
 */
export function select(asyncIterList) {
  const aq = new AsyncQueue();

  aq.enqueue();

  //  利用promise特有的观察者性质,这样可以不使用在某个周期性的重复检查
  //  一开始塞入一个堵塞,不让异步迭代器完成退出
  //  之后每当一个上游生产者退出之前检查其他生产者是否完成,如果全部生产者退出,那么让最后一个promise退出
  const isComplete = createArrayWith(asyncIterList.length, false);

  for (let i = 0; i < asyncIterList.length; i += 1) {
    const asyncIter = asyncIterList[i];

    (async function () {
      for await (const asyncIterItem of asyncIter) {
        aq.resolve({ type: i, item: asyncIterItem, isAllEnd: false });
        aq.enqueue(); //  不需要锁,因为同一时间事件循环只有一个函数会执行这里
      }

      isComplete[i] = true;

      //  每一生产者在完成后才检查一次全部流是否已经完成
      if (isComplete.every((v) => v)) {
        aq.resolve({ type: null, item: null, isAllEnd: true });
      }
    })().then(noop);
  }

  return {
    async *[Symbol.asyncIterator]() {
      for await (const { type, item, isAllEnd } of aq) {
        if (isAllEnd) {
          break;
        }

        yield [type, item];
      }
    }
  };
}

// 用于管理多个 async generator 的执行
export class CoroutineManager {
  constructor() {
    /**
     * @type {Set<AsyncGenerator | Generator>}
     */
    this.coPool = new Set();
  }

  /**
   * @param co {AsyncGenerator | Generator}
   * @returns {Promise<void>}
   */
  async startCoroutine(co) {
    if (this.coPool.has(co)) {
      console.warn("co has been started");
    }

    this.coPool.add(co);

    /**
     * @type { IteratorYieldResult | null}
     */
    let res = null;

    while ((res = await co.next(res?.value ?? null))) {
      const { done } = res;

      if (done) {
        break;
      }
    }

    // return await spawn(co);
  }

  /**
   * @description 如果返回done => true 那么说明co已经执行完成，未能成功取消
   * @param co {AsyncGenerator | Generator}
   * @returns {Promise<IteratorYieldResult>}
   */
  stopCoroutine(co) {
    this.coPool.delete(co);
    return co.return(null);
  }

  async stopAllCoroutines() {
    return Promise.allSettled([...this.coPool].map((co) => this.stopCoroutine(co)));
  }

  static WaitForMillSeconds = sleep;

  static WaitForEndOfFrame = raf;

  static WaitUntil = waitUntil;

  static async *CreateNoopCo() {}
}

/**
 * @description 包装后的函数只执行最新的操作
 * @param asyncIterFn
 * @param debugInfo
 * @returns {function(...[*]): Promise<*|{value: null, done: boolean}>}
 */
export function latestDecorator(asyncIterFn, { debugInfo = false } = {}) {
  // const { debugInfo = false } = config ?? {};

  //  闭包的上下文参数
  let latestToken = 0;

  return async function (...args) {
    latestToken += 1;
    const currentToken = latestToken;

    if (debugInfo) {
      console.log(`start >>> currentToken=${currentToken}`);
    }

    const ag = asyncIterFn(...args);

    return await spawn(ag, {
      afterYield() {
        //  当前的token和最新的不一致的时候杀掉协程
        if (currentToken !== latestToken) {
          if (debugInfo) {
            console.log(`currentToken=${currentToken}, latestToken=${latestToken}`);
          }
          return true;
        }
      }
    });
  };
}

/**
 * @description 运行一个协程,可以插入各种钩子 需要考虑执行失败的异常
 * @param ag - async generator
 * @param afterYield - 返回true则终止迭代,如果返回false或者不返回的情况则继续迭代
 */
async function spawn(ag, { afterYield = () => false } = {}) {
  let res = {
    value: null,
    done: false
  };

  while ((res = await ag.next(res.value))) {
    if (res.done) {
      break;
    }

    //  用户自定义的逻辑在每次协程的yield交还控制权的时候检查,这里类似于OS的功能
    if (afterYield()) {
      //  ag.throw()
      //  ag.return()
      break;
    }
  }

  // ag = null
  // return res;
}

export const GCM = new CoroutineManager();
